import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { applyMiddleware, combineReducers, createStore, compose as composeOrig } from 'redux';
import thunk from 'redux-thunk';
import { triggerWebpackBundle } from '@gumtree/shared/src/manifest-loader/static-js-files';
import { GrowthBookProvider } from '@growthbook/growthbook-react';

import initAds from '@gumtree/third-party-ads/init-ads';
import trackConsentState from '@gumtree/third-party-ads/src/framework/gdpr/track-consent-state';
import trackingEventsOnOneTrust from '@gumtree/third-party-ads/src/framework/gdpr/tracking-events';
import { initPermutiveWithConsent } from '@gumtree/third-party-ads/src/framework/permutive/set-permutive-consent';
import { initUtiqWithConsent } from '@gumtree/third-party-ads/src/framework/utiq/init-utiq-with-consent';
import { processPermutiveQueue } from '@gumtree/shared/src/util/permutive/queuing-system';
import iframeGaEventListener from '@gumtree/shared/src/partnerships/analytics/iframe-ga-event-listener';
import { adPerformanceTracker } from '@gumtree/third-party-ads/src/framework/tracking/ad-performance-tracker';

import 'normalize.css/normalize.css';
import '@gumtree/ui-library/src/base/base.scss';
import '@gumtree/ui-library/src/typography/typography.scss';
import { addBlockthrough } from '@gumtree/third-party-ads/src/framework/blockthrough/add-blockthrough';
import { addAdShield } from '@gumtree/third-party-ads/src/framework/ad-shield/add-ad-shield';
import { GOOGLE_CLIENT_ID } from '@gumtree/ui-library/src/google-review/config';
import { isOneTapAllowed } from '@gumtree/shared/src/util/one-tap';

import { markDuckDuckGoCookie } from '@gumtree/shared/src/util/ad-block-util';
import { initWebVitalsMonitor } from '@gumtree/shared/src/performance/web-vitals-monitor';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import createAppWith from './create-app';
import loadView from './load-view';
import initComScore from './com-score/init-com-score';

import logger from './logger';

import initGumtreeApi from './gumtree-api/init';
import qualtricsHelperInit from '../../syi/src/services/qualtrics-helper';
import { pageNames } from './route-map';
import initSentry, { captureException } from './sentry/init-sentry';
import { initForcedFeatures, initGrowthbook } from './growthbook/init-growthbook';
import { initBraze } from './braze/init-braze';
import initGoogleOneTapClient from './google-one-tap/init-google-one-tap';
import { initMonitor } from './monitor';
import { yieldToMain, executeWithYield } from './utils/yield-to-main';
import MarketingConsentModal from './marketing-consent-modal/marketing-consent-modal';

initMonitor();
triggerWebpackBundle();
/** decodes clientData after it was encoded by SSR */
window.clientData = JSON.parse(decodeURIComponent(window?.clientData));

// @ts-ignore
const { clientData: data, growthbookExperimentData, experimentOverrides } = window;

const { page, baseConfig, userData } = data;

const parsedExperimentData = JSON.parse(decodeURIComponent(growthbookExperimentData));
const growthbook = initGrowthbook(
    parsedExperimentData,
    page.appViewDevice,
    page.gbUserPseudoId,
    true
);
const isMobile = baseConfig.device?.type === 'mobile';
window.growthbook = growthbook;

// Build the list of non-critical initialization tasks that can be yielded
const getDeferredInitTasks = () => [
    // Utilities initialization
    () => initGumtreeApi(window),
    () => qualtricsHelperInit(window),

    // Permutive (has dependencies, must run in order)
    () => initPermutiveWithConsent(window),
    () => processPermutiveQueue(window),
    // Utiq Integrated Model (two-way consent sync with OneTrust)
    () => initUtiqWithConsent(baseConfig.domain),

    // Ads performance tracking
    () => {
        (window as any).adPerformanceTracker = adPerformanceTracker;
        if ((window as any)._gptRequestStartTime) {
            adPerformanceTracker.setGPTScriptRequestStartTime((window as any)._gptRequestStartTime);
        } else {
            adPerformanceTracker.markGPTScriptRequestStart();
        }
        adPerformanceTracker.initGPTLoadTracking();
    },

    () => {
        initBraze({
            apiKey: baseConfig.brazeApiKey,
            baseUrl: baseConfig.brazeBaseUrl,
            enableLogging: baseConfig.isDev,
            user: userData,
        }).then((braze) => {
            if (braze) {
                (window as any).braze = braze;
            }
        });
    },

    () => {
        const allowOneTap = isOneTapAllowed(page.type);
        if (allowOneTap && !(window as any).Cypress) {
            initGoogleOneTapClient({
                clientId: baseConfig.google_app_id || GOOGLE_CLIENT_ID,
                isLoggedIn: Boolean(userData?.userLoggedIn),
                isMobile,
            });
        }
    },

    () => {
        Math.random() < 0.001 && trackConsentState(window);
    },
    () => trackingEventsOnOneTrust(window),
    () => iframeGaEventListener(),

    () => initComScore(window),
    () => {
        initWebVitalsMonitor({
            pageType: baseConfig.pageType,
            deviceType: baseConfig.device?.type,
            clsThreshold: 0.01,
            inpThreshold: 200,
            enableDebugLog: false,
        });
    },
];

const parsedExperimentOverrides = JSON.parse(decodeURIComponent(experimentOverrides));
initForcedFeatures(growthbook, parsedExperimentOverrides);

export const runNonCriticalInitAndAds = async () => {
    await yieldToMain().catch(() => {});

    initSentry(baseConfig.isDev, baseConfig.environment, baseConfig.sentryDSN);

    if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(
            async () => {
                await executeWithYield(getDeferredInitTasks(), { yieldBetween: true });
            },
            { timeout: 3000 }
        );
    } else {
        setTimeout(async () => {
            await executeWithYield(getDeferredInitTasks(), { yieldBetween: true });
        }, 100);
    }

    initAds(window as any);
    await yieldToMain().catch(() => {});
};

void runNonCriticalInitAndAds();

const composeEnhancers =
    (baseConfig.showReduxDevTools &&
        typeof window !== 'undefined' &&
        // @ts-ignore
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
    composeOrig;

// initComScore and initWebVitalsMonitor run only in getDeferredInitTasks() to avoid duplicate work and a longer sync block

window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
});

function unregisterServiceWorker() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker
            .getRegistrations()
            .then(function (registrations) {
                for (const registration of registrations) {
                    registration.unregister();
                }
            })
            .catch(function (err) {
                logger.error('Service Worker registration failed: ', err);
            });
    }
}

// @ts-ignore
loadView(page.type).then(async ({ App, reducers }) => {
    page.App = App;

    const middlewares = [thunk];
    const store = createStore(
        combineReducers({ request: (state = {}) => state, ...reducers }),
        data,
        composeEnhancers(applyMiddleware(...middlewares))
    );

    unregisterServiceWorker();

    try {
        hydrateRoot(
            document.querySelector('#content') as HTMLElement,
            <GrowthBookProvider growthbook={growthbook}>
                <BrowserRouter>{createAppWith(store)}</BrowserRouter>
            </GrowthBookProvider>
        );
    } catch (e) {
        captureException(e);
        throw e;
    }

    // Yield after hydration so the browser can process any pending user interactions
    // before running post-hydration work (lazysizes, blockthrough, etc.)
    await yieldToMain().catch(() => {});

    if (typeof window !== 'undefined') {
        import('lazysizes').then((lazy) => lazy.init());
    }

    if (window.Cypress) {
        // @ts-ignore
        window.store = store;
    }

    markDuckDuckGoCookie(window);

    const runAdShield = growthbook.getFeatureValue(GrowthBookFeature.AD02, 'OFF') === 'B';
    const deferAdShield = growthbook.getFeatureValue(GrowthBookFeature.AD02_2S, 'OFF') === 'B';

    await yieldToMain().catch(() => {});

    if (runAdShield) {
        if ([pageNames.HOME, pageNames.SRP, pageNames.VIP].includes(page.type)) {
            if (deferAdShield) {
                if (typeof requestIdleCallback === 'undefined') {
                    setTimeout(() => addAdShield(), 2000);
                } else {
                    requestIdleCallback(() => addAdShield(), { timeout: 2000 });
                }
            } else {
                addAdShield();
            }
        }
    } else {
        if (page.type === pageNames.SRP) {
            addBlockthrough(window);
        }
    }

    await yieldToMain().catch(() => {});

    if (userData?.userLoggedIn && userData?.email) {
        const modalRoot = document.createElement('div');
        modalRoot.id = 'marketing-consent-root';
        document.body.appendChild(modalRoot);
        createRoot(modalRoot).render(
            <MarketingConsentModal
                email={userData.email}
                isMobile={isMobile}
                sellerUrl={baseConfig.sellerUrl}
            />
        );
    }
});
